import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../../lib/supabaseClient';
import axios from 'axios';

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

export async function createPaymentSession(
  amount: number,
  description: string,
  currencyCode: string = 'USD',
  methodId: string,
  gatewayId: string,
  planId?: string
) {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    // Get method details
    const { data: method, error: methodError } = await supabase
      .from('payment_methods')
      .select('name, metadata, gateway:gateway_id(id, name)')
      .eq('id', methodId)
      .single();

    if (methodError) {
      console.error('Method error:', methodError);
      throw new Error('Invalid payment method');
    }

    // Get plan details
    const { data: plan, error: planError } = await supabase
      .from('subscription_plans')
      .select('name')
      .eq('id', planId)
      .single();

    if (planError) {
      console.error('Plan error:', planError);
      throw new Error('Invalid subscription plan');
    }

    // Get gateway name for better error handling
    const gatewayName = method.gateway?.name || 'Unknown';

    // Check if method uses direct link or plan ID
    const useDirectLink = method.metadata?.useDirectLink === true;
    const useDirectPayment = method.metadata?.useDirectPayment === true;
    
    if (useDirectLink) {
      // Handle direct link method
      return handleDirectLinkPayment(method, planId, user.email, gatewayName);
    } else if (useDirectPayment) {
      // Handle direct payment method
      return await createDirectPayment(amount, description, user.email, currencyCode, methodId, planId);
    } else {
      // Get the external plan ID from the method's metadata
      const externalPlanIds = method.metadata?.planIds || {};
      const externalPlanId = planId ? externalPlanIds[planId] : null;
      
      if (!externalPlanId) {
        throw new Error(`No external plan ID configured for ${plan.name} with ${method.name}`);
      }
      
      // Handle plan ID method based on gateway
      if (gatewayName.toLowerCase().includes('paypal')) {
        return await createPayPalPayment(amount, description, user.email, currencyCode, methodId, planId, externalPlanId);
      } else if (gatewayName.toLowerCase().includes('paystack')) {
        return await createPaystackPayment(amount, description, user.email, currencyCode, methodId, planId, externalPlanId);
      } else if (gatewayName.toLowerCase().includes('stripe')) {
        return await createStripePayment(amount, description, user.email, currencyCode, methodId, planId, externalPlanId);
      } else {
        throw new Error(`Unsupported payment gateway: ${gatewayName}`);
      }
    }
  } catch (error) {
    console.error('Payment error:', error);
    
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('An unexpected error occurred during payment processing');
    }
  }
}

async function handleDirectLinkPayment(
  method: any,
  planId?: string,
  email?: string,
  gatewayName?: string
) {
  // Check if we have a subscription URL for this plan
  const subscriptionUrls = method.metadata?.subscriptionUrls || {};
  const subscriptionUrl = subscriptionUrls[planId || ''];

  if (!subscriptionUrl) {
    throw new Error(`No subscription URL configured for this plan with ${method.name}`);
  }

  // Add user email as a query parameter for identification
  const urlWithParams = new URL(subscriptionUrl);
  if (email) {
    urlWithParams.searchParams.append('email', email);
  }
  
  // Record the payment attempt
  if (email) {
    await supabase.from('payment_orders').insert({
      user_id: email,
      order_id: `${gatewayName}_${method.id}_${Date.now()}`,
      payment_provider: gatewayName || 'direct',
      amount: 0, // Amount is handled by the external provider
      currency: 'USD', // Default currency
      description: 'Subscription via direct link',
      status: 'created',
      metadata: {
        method_id: method.id,
        plan_id: planId,
        email: email,
        subscription_url: subscriptionUrl
      }
    });
  }
  
  // Open the subscription URL in a new tab
  window.open(urlWithParams.toString(), '_blank');
  
  return { success: true };
}

async function createDirectPayment(
  amount: number,
  description: string,
  email: string,
  currencyCode: string,
  methodId: string,
  planId?: string
) {
  try {
    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');

    // Create payment through backend API
    const response = await axios.post('/api/payments/create-direct-payment', {
      amount,
      description,
      email,
      currency: currencyCode.toLowerCase(),
      methodId,
      planId
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      }
    });

    if (!response.data.checkoutUrl) {
      throw new Error('Failed to create payment checkout');
    }

    // Use window.open for better compatibility with popup blockers
    window.open(response.data.checkoutUrl, '_self');
    return { success: true };
  } catch (error) {
    console.error('Direct payment error:', error);
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.error || 'Failed to create direct payment');
    }
    throw error;
  }
}

async function createStripePayment(
  amount: number,
  description: string,
  email: string,
  currencyCode: string,
  methodId: string,
  planId?: string,
  externalPlanId?: string
) {
  // Get auth token
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No active session');

  // Create payment through backend API
  const response = await axios.post('/api/payments/create-payment', {
    amount,
    description,
    email,
    currency: currencyCode.toLowerCase(),
    methodId,
    planId,
    externalPlanId // Send the external plan ID to the backend
  }, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`
    }
  });

  if (!response.data.sessionId) {
    throw new Error('Failed to create payment session');
  }

  // Redirect to Stripe Checkout
  const stripe = await stripePromise;
  if (!stripe) throw new Error('Failed to load Stripe');

  const { error } = await stripe.redirectToCheckout({
    sessionId: response.data.sessionId
  });

  if (error) throw error;
  
  return { success: true };
}

async function createPayPalPayment(
  amount: number,
  description: string,
  email: string,
  currencyCode: string,
  methodId: string,
  planId?: string,
  externalPlanId?: string
) {
  try {
    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');

    // Create payment through backend API
    const response = await axios.post('/api/payments/create-paypal-payment', {
      amount,
      description,
      email,
      currency: currencyCode,
      methodId,
      planId,
      externalPlanId // Send the external plan ID to the backend
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      }
    });

    // Open PayPal approval URL in new tab
    if (response.data.approvalUrl) {
      window.open(response.data.approvalUrl, '_blank');
      return { success: true, paymentId: response.data.paymentId };
    } else {
      throw new Error('No approval URL returned from PayPal');
    }
  } catch (error) {
    console.error('PayPal payment error:', error);
    if (axios.isAxiosError(error) && error.response) {
      if (error.response?.status === 429) {
        throw new Error('Too many payment attempts. Please wait a moment before trying again.');
      }
     
      // Redirect to success page for testing if needed
      if (process.env.NODE_ENV === 'development') {
        window.location.href = '/payment/success';
        return { success: true };
      }
      
      throw error;
    }
    throw error;
  }
}

async function createPaystackPayment(
  amount: number,
  description: string,
  email: string,
  currencyCode: string,
  methodId: string,
  planId?: string,
  externalPlanId?: string
) {
  try {
    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');

    // Create payment through backend API
    const response = await axios.post('/api/payments/create-paystack-payment', {
      amount,
      description,
      email,
      currency: currencyCode,
      methodId,
      planId,
      externalPlanId // Send the external plan ID to the backend
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      }
    });

    if (!response.data.authorizationUrl) {
      throw new Error('Failed to create Paystack payment');
    }

    // Open Paystack authorization URL in new tab
    window.open(response.data.authorizationUrl, '_blank');
    return { success: true, reference: response.data.reference };
  } catch (error) {
    console.error('Paystack payment error:', error);
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.error || 'Failed to create Paystack payment');
    }
    throw error;
  }
}